import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import PauseRounded from '@mui/icons-material/PauseRounded';
import CropSquareRounded from '@mui/icons-material/CropSquareRounded';
import ViewColumnRounded from '@mui/icons-material/ViewColumnRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ClearRounded from '@mui/icons-material/ClearRounded';
import InvertColorsRounded from '@mui/icons-material/InvertColorsRounded';
import InvertColorsOffRounded from '@mui/icons-material/InvertColorsOffRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';

export interface CompareInstance {
  url: string;
  view?: string;
  stage?: string;
  // Acquisition order from the cart; used by the overview's protocol-order suggestions.
  instanceNumber?: string | number;
  seriesNumber?: string | number;
  // Study axis (Compare Previous Studies). Absent for single-study compare.
  studyInstanceUID?: string;
  studyLabel?: string;
  isCurrent?: boolean;
}

export interface PriorStudyOption {
  studyInstanceUID: string;
  label: string;
  loaded?: boolean;
}

interface CompareOverlayProps {
  open: boolean;
  onClose: () => void;
  instances: CompareInstance[];
  getPlaybackRate?: (url: string) => number;
  initialUrl?: string;
  priorStudies?: PriorStudyOption[];
  onLoadPriorStudy?: (studyInstanceUID: string) => void;
  loadingPriorUID?: string | null;
}

const VIEW_DISPLAY_MAP: Record<string, string> = {
  '4CH': 'AP4',
  '2CH': 'AP2',
  APLAX: 'AP3',
  PLAX: 'LAX',
  'SAX-PM': 'SAX',
  ASX: 'pSAX-ap1',
};

type LayoutSize = 1 | 2 | 4;

// Palette — theme navy/teal. Current study = teal; each prior gets a distinct hue.
// Colour is ALWAYS paired with the study's date label (never colour-alone).
const NAVY = '#00101f';
const PANEL = '#0b1b2c';
const TILE_BG = '#04121f';
const HAIRLINE = 'rgba(122,160,199,0.22)';
const CURRENT_COLOR = '#2dd4bf';
const PRIOR_COLORS = ['#f59e0b', '#a78bfa', '#60a5fa', '#f472b6', '#34d399', '#fb7185', '#38bdf8'];

const hasView = (inst: CompareInstance) =>
  !!inst.view && inst.view.trim() !== '' && inst.view.toUpperCase() !== 'NA';

const viewLabel = (inst: CompareInstance) =>
  hasView(inst) ? VIEW_DISPLAY_MAP[inst.view as string] || (inst.view as string) : '';

const studyKeyOf = (inst: CompareInstance) => inst.studyInstanceUID || inst.studyLabel || 'current';

const CompareOverlay = ({
  open,
  onClose,
  instances,
  getPlaybackRate,
  initialUrl,
  priorStudies,
  onLoadPriorStudy,
  loadingPriorUID,
}: CompareOverlayProps) => {
  const [layout, setLayout] = useState<LayoutSize>(2);
  const [slots, setSlots] = useState<(CompareInstance | null)[]>([null, null, null, null]);
  const [isPlaying, setIsPlaying] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // The tile the keyboard acts on. Clicking a tile makes it active; tile 1 is the
  // default so the arrow keys work immediately without the doctor having to click
  // anything first.
  const [activeSlot, setActiveSlot] = useState(0);
  /**
   * Colour suppression, per tile.
   *
   * Dr Shashi's ask: turn colour off on one pane while the other keeps it, to see where a
   * regurgitant jet originates. On the cart this is a true toggle because colour is a
   * separate data plane composited over B-mode. WE DO NOT RECEIVE THAT PLANE — the express
   * viewer plays JPEG/WebM with colour already burned into the pixels, so the tissue the
   * jet covers is genuinely gone and no code can recover it.
   *
   * What `saturate(0)` does give is exact where it matters: greyscale tissue has R=G=B, and
   * the luminance matrix maps such a pixel to itself, so the anatomy is untouched
   * bit-for-bit. Only the coloured pixels change, collapsing to their own brightness. Good
   * enough to judge whether a jet sits over the leaflet or the annulus; NOT a way to read
   * structure hidden beneath a dense mosaic jet.
   */
  const [suppressed, setSuppressed] = useState<boolean[]>([false, false, false, false]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  // Studies present in the loaded instances: current first, then priors in order,
  // each assigned a stable accent colour + its loops (sub-info kept on the item).
  const studies = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        label: string;
        isCurrent: boolean;
        color: string;
        items: { inst: CompareInstance; idx: number }[];
      }
    >();
    instances.forEach((inst, idx) => {
      const key = studyKeyOf(inst);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: inst.studyLabel || (inst.isCurrent ? 'Current study' : 'Study'),
          isCurrent: !!inst.isCurrent,
          color: CURRENT_COLOR,
          items: [],
        });
      }
      map.get(key)!.items.push({ inst, idx });
    });
    const arr = [...map.values()];
    arr.sort((a, b) => (a.isCurrent ? 0 : 1) - (b.isCurrent ? 0 : 1));
    let p = 0;
    arr.forEach(s => {
      s.color = s.isCurrent ? CURRENT_COLOR : PRIOR_COLORS[p++ % PRIOR_COLORS.length];
    });
    return arr;
  }, [instances]);

  // One authoritative accent colour per study key — current = teal, each prior a
  // stable hue by its order in the picker — so lane headers, thumbnails and tile
  // badges all agree. Keyed by studyInstanceUID (picker) + current study key.
  const colorByKey = useMemo(() => {
    const m: Record<string, string> = {};
    const cur = studies.find(s => s.isCurrent);
    if (cur) {
      m[cur.key] = CURRENT_COLOR;
    }
    (priorStudies || []).forEach((ps, i) => {
      m[ps.studyInstanceUID] = PRIOR_COLORS[i % PRIOR_COLORS.length];
    });
    return m;
  }, [studies, priorStudies]);

  // studyKey → { color, label } for tile provenance badges.
  const studyMeta = useMemo(() => {
    const m: Record<string, { color: string; label: string; isCurrent: boolean }> = {};
    studies.forEach(
      s =>
        (m[s.key] = { color: colorByKey[s.key] || s.color, label: s.label, isCurrent: s.isCurrent })
    );
    return m;
  }, [studies, colorByKey]);

  const metaFor = (inst: CompareInstance) =>
    studyMeta[studyKeyOf(inst)] || { color: '#94a3b8', label: '', isCurrent: false };

  // Unified study list for the rail: the current study, then every prior (loaded
  // or not). One row per study — click loads it (if needed) + expands its loops.
  const lanes = useMemo(() => {
    const out: {
      key: string;
      label: string;
      isCurrent: boolean;
      color: string;
      loaded: boolean;
      loading: boolean;
      items: { inst: CompareInstance; idx: number }[];
    }[] = [];
    const cur = studies.find(s => s.isCurrent);
    if (cur) {
      out.push({ ...cur, color: colorByKey[cur.key] || cur.color, loaded: true, loading: false });
    }
    (priorStudies || []).forEach(ps => {
      const loadedStudy = studies.find(s => s.key === ps.studyInstanceUID);
      out.push({
        key: ps.studyInstanceUID,
        label: ps.label,
        isCurrent: false,
        color: colorByKey[ps.studyInstanceUID] || '#94a3b8',
        loaded: !!loadedStudy,
        loading: loadingPriorUID === ps.studyInstanceUID,
        items: loadedStudy ? loadedStudy.items : [],
      });
    });
    return out;
  }, [studies, priorStudies, colorByKey, loadingPriorUID]);

  const multiStudy = studies.length > 1 || (priorStudies?.length || 0) > 0;

  const onLaneClick = (lane: { key: string; loaded: boolean; loading: boolean }) => {
    if (!lane.loaded) {
      if (!lane.loading && onLoadPriorStudy) {
        onLoadPriorStudy(lane.key);
      }
      setCollapsed(c => ({ ...c, [lane.key]: false }));
    } else {
      setCollapsed(c => ({ ...c, [lane.key]: !c[lane.key] }));
    }
  };

  // Reset on open; seed tile 0 with the loop the doctor was viewing.
  useEffect(() => {
    if (!open) {
      return;
    }
    const seeded: (CompareInstance | null)[] = [null, null, null, null];
    if (initialUrl) {
      const current = instances.find(i => i.url === initialUrl);
      if (current) {
        seeded[0] = current;
      }
    }
    setSlots(seeded);
    setIsPlaying(true);
  }, [open, initialUrl, instances]);

  useEffect(() => {
    videoRefs.current.forEach(video => {
      if (!video) {
        return;
      }
      if (isPlaying) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [isPlaying, slots, layout]);

  const applyRate = (el: HTMLVideoElement | null, url: string) => {
    if (!el || !getPlaybackRate) {
      return;
    }
    const rate = getPlaybackRate(url);
    if (typeof rate === 'number' && rate > 0) {
      el.playbackRate = rate;
    }
  };

  // Re-apply rates when they resolve after the slot videos already loaded.
  useEffect(() => {
    videoRefs.current.forEach((video, slotIdx) => {
      const url = slots[slotIdx]?.url;
      if (video && url) {
        applyRate(video, url);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, layout, getPlaybackRate]);

  const placeInSlot = (slotIdx: number, inst: CompareInstance) =>
    setSlots(prev => {
      const next = [...prev];
      next[slotIdx] = inst;
      return next;
    });

  const toggleSuppressed = (slotIdx: number) =>
    setSuppressed(prev => {
      const next = [...prev];
      next[slotIdx] = !next[slotIdx];
      return next;
    });

  const clearSlot = (slotIdx: number) => {
    setSlots(prev => {
      const next = [...prev];
      next[slotIdx] = null;
      return next;
    });
    // An emptied tile starts fresh — otherwise the next clip dropped in would arrive
    // silently desaturated and read as a greyscale acquisition.
    setSuppressed(prev => {
      if (!prev[slotIdx]) {
        return prev;
      }
      const next = [...prev];
      next[slotIdx] = false;
      return next;
    });
  };

  const swapSlots = (a: number, b: number) => {
    if (a === b) {
      return;
    }
    setSlots(prev => {
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  };

  const addToFirstEmpty = (inst: CompareInstance) => {
    const emptyIdx = slots.findIndex((s, i) => i < layout && s === null);
    if (emptyIdx !== -1) {
      placeInSlot(emptyIdx, inst);
    }
  };

  // A tile cycles within its OWN study — same view when labelled, whole study otherwise.
  const candidatesFor = (inst: CompareInstance) => {
    const key = studyKeyOf(inst);
    const sameStudy = instances.filter(i => studyKeyOf(i) === key);
    return hasView(inst) ? sameStudy.filter(i => i.view === inst.view) : sameStudy;
  };

  const slotPosition = (inst: CompareInstance) => {
    const list = candidatesFor(inst);
    return { pos: list.findIndex(i => i.url === inst.url) + 1, total: list.length };
  };

  const cycleSlot = (slotIdx: number, dir: 1 | -1) => {
    const inst = slots[slotIdx];
    if (!inst) {
      return;
    }
    const list = candidatesFor(inst);
    if (list.length < 2) {
      return;
    }
    const cur = list.findIndex(i => i.url === inst.url);
    placeInSlot(slotIdx, list[(cur + dir + list.length) % list.length]);
  };

  /**
   * Arrow keys step the ACTIVE tile through its loops, matching the quad view.
   * The express page suppresses its own arrow handling while this overlay is open,
   * so without this the keys did nothing here at all.
   * Tab/Shift+Tab moves which tile is active, so the whole grid is keyboard-drivable.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      // Never hijack typing in an input the overlay may grow later.
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        cycleSlot(activeSlot, e.key === 'ArrowRight' ? 1 : -1);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSlot(prev => (prev + (e.key === 'ArrowDown' ? 1 : -1) + layout) % layout);
        return;
      }
      // Colour on/off for the active tile — the comparison is a flick back and forth, so
      // it needs to be one key rather than a trip to a button.
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        toggleSuppressed(activeSlot);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // cycleSlot is re-created every render; the values it actually closes over
    // (slots, instances) are listed instead, so the handler is never stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeSlot, layout, slots, instances]);

  // Shrinking the grid must not leave the keyboard pointed at a tile that is no
  // longer rendered.
  useEffect(() => {
    setActiveSlot(prev => (prev >= layout ? 0 : prev));
  }, [layout]);

  // Preset: current study vs the first prior — seeds a 2-up, same view when possible.
  const handleCurrentVsPrior = () => {
    const current = studies.find(s => s.isCurrent);
    const prior = studies.find(s => !s.isCurrent);
    if (!current || !prior) {
      return;
    }
    const seed = initialUrl ? current.items.find(x => x.inst.url === initialUrl)?.inst : undefined;
    const left = seed || current.items[0]?.inst || null;
    // Match the prior study's same view if it has one, else its first loop.
    const leftView = left ? left.view : undefined;
    const right =
      (leftView && prior.items.find(x => x.inst.view === leftView)?.inst) ||
      prior.items[0]?.inst ||
      null;
    setLayout(2);
    setSlots([left, right, null, null]);
  };

  const handleThumbDragStart = (e: React.DragEvent, instIdx: number) => {
    e.dataTransfer.setData('text/plain', `thumb:${instIdx}`);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const handleSlotDragStart = (e: React.DragEvent, slotIdx: number) => {
    e.dataTransfer.setData('text/plain', `slot:${slotIdx}`);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleSlotDrop = (e: React.DragEvent, slotIdx: number) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data.startsWith('thumb:')) {
      const instIdx = Number(data.slice(6));
      if (!Number.isNaN(instIdx) && instances[instIdx]) {
        placeInSlot(slotIdx, instances[instIdx]);
      }
    } else if (data.startsWith('slot:')) {
      const fromIdx = Number(data.slice(5));
      if (!Number.isNaN(fromIdx)) {
        swapSlots(fromIdx, slotIdx);
      }
    }
  };

  const gridTemplate =
    layout === 1
      ? { columns: '1fr', rows: '1fr' }
      : layout === 2
        ? { columns: '1fr 1fr', rows: '1fr' }
        : { columns: '1fr 1fr', rows: '1fr 1fr' };

  const renderMedia = (inst: CompareInstance, slotIdx: number) => {
    const mediaStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      ...(suppressed[slotIdx] ? { filter: 'saturate(0)' } : {}),
    };

    if (inst.url.endsWith('.webm')) {
      return (
        <video
          key={inst.url}
          ref={el => (videoRefs.current[slotIdx] = el)}
          autoPlay
          loop
          controls
          disablePictureInPicture
          controlsList="nofullscreen"
          onLoadedData={e => applyRate(e.currentTarget, inst.url)}
          style={mediaStyle}
        >
          <source
            src={inst.url}
            type="video/webm"
          />
        </video>
      );
    }
    return (
      <img
        src={inst.url}
        alt={viewLabel(inst) || `Compare ${slotIdx + 1}`}
        style={mediaStyle}
      />
    );
  };

  const layoutBtnSx = {
    color: '#94a3b8',
    border: `1px solid ${HAIRLINE}`,
    px: 1.1,
    '&.Mui-selected': { color: NAVY, bgcolor: CURRENT_COLOR },
    '&.Mui-selected:hover': { bgcolor: CURRENT_COLOR },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,16,31,0.94)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
          color: '#e2e8f0',
        },
      }}
      BackdropProps={{ sx: { backgroundColor: 'transparent' } }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${HAIRLINE}`,
          flexShrink: 0,
        }}
      >
        <CompareArrowsRounded sx={{ color: CURRENT_COLOR }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, mr: 1 }}>Compare Studies</Typography>

        <ToggleButtonGroup
          value={layout}
          exclusive
          size="small"
          onChange={(_, v) => v && setLayout(v)}
          sx={{ '& .MuiToggleButton-root': layoutBtnSx }}
        >
          <ToggleButton
            value={1}
            aria-label="Single view"
          >
            <Tooltip title="1 view">
              <CropSquareRounded fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value={2}
            aria-label="Two views side by side"
          >
            <Tooltip title="2 views">
              <ViewColumnRounded fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value={4}
            aria-label="Four views"
          >
            <Tooltip title="4 views">
              <GridViewRounded fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {multiStudy && (
          <Tooltip title="Seed a 2-up: current study vs the first prior (same view)">
            <Button
              size="small"
              startIcon={<CompareArrowsRounded />}
              onClick={handleCurrentVsPrior}
              sx={{
                color: '#fff',
                border: `1px solid ${HAIRLINE}`,
                textTransform: 'none',
                px: 1.5,
              }}
            >
              Current vs Prior
            </Button>
          </Tooltip>
        )}

        <IconButton
          onClick={() => setIsPlaying(p => !p)}
          sx={{ color: '#fff', border: `1px solid ${HAIRLINE}`, borderRadius: 1 }}
          aria-label={isPlaying ? 'Pause all' : 'Play all'}
        >
          {isPlaying ? <PauseRounded /> : <PlayArrowRounded />}
        </IconButton>

        <Typography sx={{ fontSize: 12, color: '#7c93ac', ml: 'auto', mr: 1, maxWidth: 360 }}>
          Drag a loop into a tile · ← → steps the selected tile (↑ ↓ or click selects) · C
          suppresses colour · drag a tile header to swap
        </Typography>

        <IconButton
          onClick={onClose}
          aria-label="Close compare"
          sx={{
            color: '#fff',
            borderRadius: '50%',
            bgcolor: 'rgba(239,68,68,0.18)',
            border: '1px solid rgba(239,68,68,0.6)',
            '&:hover': { bgcolor: 'rgba(239,68,68,0.34)' },
          }}
        >
          <CloseRounded />
        </IconButton>
      </Box>

      {/* Body: source rail + battleground */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Source rail */}
        <Box
          sx={{
            width: 232,
            flexShrink: 0,
            overflowY: 'auto',
            borderRight: `1px solid ${HAIRLINE}`,
            p: 1,
            bgcolor: PANEL,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              px: 0.5,
              flexShrink: 0,
            }}
          >
            Studies
          </Typography>

          {instances.length === 0 && (
            <Typography sx={{ fontSize: 12, color: '#7c93ac', textAlign: 'center', mt: 2 }}>
              No media found
            </Typography>
          )}

          {/* Unified study list — one row per study; click loads + expands loops */}
          {lanes.map(lane => {
            const expanded = lane.loaded && !collapsed[lane.key];
            return (
              <Box
                key={lane.key}
                sx={{ flexShrink: 0 }}
              >
                <Box
                  onClick={() => onLaneClick(lane)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.5,
                    py: 0.6,
                    cursor: 'pointer',
                    borderLeft: `3px solid ${lane.color}`,
                    borderRadius: 0.5,
                    bgcolor: expanded ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                    mb: 0.5,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      bgcolor: lane.color,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.2 }}
                      noWrap
                    >
                      {lane.label}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                      {lane.loading
                        ? 'Loading…'
                        : lane.loaded
                          ? `${lane.isCurrent ? 'Current' : 'Prior'} · ${lane.items.length} loops`
                          : 'Click to load'}
                    </Typography>
                  </Box>
                  <ChevronRightRounded
                    fontSize="small"
                    sx={{
                      color: '#64748b',
                      transform: expanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 120ms',
                    }}
                  />
                </Box>
                <Collapse in={expanded}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, pb: 0.5 }}>
                    {lane.items.map(({ inst, idx }) => (
                      <Box
                        key={`${inst.url}-${idx}`}
                        draggable
                        onDragStart={e => handleThumbDragStart(e, idx)}
                        onClick={() => addToFirstEmpty(inst)}
                        sx={{
                          position: 'relative',
                          cursor: 'grab',
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: `1px solid ${HAIRLINE}`,
                          borderLeft: `3px solid ${lane.color}`,
                          '&:hover': { borderColor: lane.color },
                        }}
                      >
                        {inst.url.endsWith('.webm') ? (
                          <video
                            src={inst.url}
                            preload="metadata"
                            muted
                            style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
                          />
                        ) : (
                          <img
                            src={inst.url}
                            alt={viewLabel(inst) || `Loop ${idx + 1}`}
                            style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
                          />
                        )}
                        {viewLabel(inst) && (
                          <Chip
                            label={viewLabel(inst)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 3,
                              left: 3,
                              height: 17,
                              fontSize: 9.5,
                              color: '#fff',
                              bgcolor: 'rgba(4,18,31,0.85)',
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>

        {/* Battleground */}
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: gridTemplate.columns,
            gridTemplateRows: gridTemplate.rows,
            gap: 1,
            p: 1,
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {Array.from({ length: layout }).map((_, slotIdx) => {
            const inst = slots[slotIdx];
            const meta = inst ? metaFor(inst) : null;
            return (
              <Box
                key={slotIdx}
                onDragOver={e => e.preventDefault()}
                onDrop={e => handleSlotDrop(e, slotIdx)}
                onMouseDown={() => setActiveSlot(slotIdx)}
                sx={{
                  position: 'relative',
                  bgcolor: TILE_BG,
                  border: `1px solid ${HAIRLINE}`,
                  borderLeft: meta ? `4px solid ${meta.color}` : `1px solid ${HAIRLINE}`,
                  borderRadius: 1,
                  // Only mark the active tile once more than one tile is on screen —
                  // in a 1-up the ring would be noise.
                  outline:
                    layout > 1 && slotIdx === activeSlot ? `2px solid ${CURRENT_COLOR}` : 'none',
                  outlineOffset: '-2px',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  '&:hover .cmp-step': { opacity: 1 },
                }}
              >
                {/* Tile header — provenance badge (colour + study date + view) */}
                <Box
                  draggable={!!inst}
                  onDragStart={e => inst && handleSlotDragStart(e, slotIdx)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.35,
                    borderBottom: `1px solid ${HAIRLINE}`,
                    cursor: inst ? 'grab' : 'default',
                    flexShrink: 0,
                    minHeight: 30,
                  }}
                >
                  {inst && <DragIndicatorRounded sx={{ fontSize: 16, color: '#64748b' }} />}
                  {inst && meta && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: meta.color,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography
                    sx={{ fontSize: 11.5, color: '#cbd5e1', fontWeight: 600 }}
                    noWrap
                  >
                    {inst
                      ? `${meta?.label || ''}${viewLabel(inst) ? ` · ${viewLabel(inst)}` : ''}`
                      : `Tile ${slotIdx + 1}`}
                  </Typography>
                  {inst && (
                    <Tooltip
                      title={
                        suppressed[slotIdx]
                          ? 'Show colour Doppler (C)'
                          : 'Suppress colour Doppler (C)'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          setActiveSlot(slotIdx);
                          toggleSuppressed(slotIdx);
                        }}
                        sx={{
                          ml: 'auto',
                          color: suppressed[slotIdx] ? CURRENT_COLOR : '#94a3b8',
                          p: 0.25,
                        }}
                        aria-label={
                          suppressed[slotIdx] ? 'Show colour Doppler' : 'Suppress colour Doppler'
                        }
                      >
                        {suppressed[slotIdx] ? (
                          <InvertColorsOffRounded fontSize="small" />
                        ) : (
                          <InvertColorsRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {inst && (
                    <IconButton
                      size="small"
                      onClick={() => clearSlot(slotIdx)}
                      sx={{
                        color: '#94a3b8',
                        p: 0.25,
                      }}
                      aria-label="Remove from compare"
                    >
                      <ClearRounded fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {/* Media */}
                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                  }}
                >
                  {inst ? (
                    renderMedia(inst, slotIdx)
                  ) : (
                    <Typography sx={{ fontSize: 13, color: '#475569' }}>
                      Drag a loop here
                    </Typography>
                  )}

                  {/* A suppressed pane must never be mistakable for a study acquired
                      without colour — the difference changes what the image means. */}
                  {inst && suppressed[slotIdx] && (
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        px: 0.9,
                        py: 0.2,
                        borderRadius: 5,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: NAVY,
                        bgcolor: CURRENT_COLOR,
                        pointerEvents: 'none',
                      }}
                    >
                      COLOUR OFF
                    </Typography>
                  )}

                  {/* Steppers sit ON the image like the quad view. The header pair was
                      16px grey glyphs in a 30px strip and read as decoration. */}
                  {inst &&
                    (() => {
                      const { pos, total } = slotPosition(inst);
                      if (total < 2) {
                        return null;
                      }
                      const arrow = (dir: 1 | -1) => (
                        <IconButton
                          className="cmp-step"
                          onClick={e => {
                            e.stopPropagation();
                            setActiveSlot(slotIdx);
                            cycleSlot(slotIdx, dir);
                          }}
                          aria-label={dir === 1 ? 'Next loop' : 'Previous loop'}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            [dir === 1 ? 'right' : 'left']: 6,
                            width: 38,
                            height: 38,
                            color: '#e2e8f0',
                            bgcolor: 'rgba(6,18,34,0.62)',
                            border: `1px solid ${HAIRLINE}`,
                            // Visible at rest on touch/first glance, solid on hover.
                            opacity: 0.75,
                            transition: 'opacity .15s, background-color .15s',
                            '&:hover': { bgcolor: 'rgba(6,18,34,0.9)' },
                          }}
                        >
                          {dir === 1 ? <ChevronRightRounded /> : <ChevronLeftRounded />}
                        </IconButton>
                      );
                      return (
                        <>
                          {arrow(-1)}
                          {arrow(1)}
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              px: 0.9,
                              py: 0.2,
                              borderRadius: 5,
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#cbd5e1',
                              bgcolor: 'rgba(6,18,34,0.62)',
                            }}
                          >
                            {pos}/{total}
                          </Typography>
                        </>
                      );
                    })()}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Dialog>
  );
};

export default CompareOverlay;
